// Pure helpers that drive the Tool audit log filter section.
//
// The audit log page narrows the "Tool" dropdown to only the tools owned
// by the picked integration, and silently falls back to "all tools" when
// the user switches integrations after picking a specific tool — so we
// never send a stale `toolId` that no longer matches the integration
// filter and end up with an empty-looking audit table.
//
// The same "stale id never leaks" rule is applied to the Bot and User
// dropdowns: if an admin picks a chatbot or user and that row is later
// deleted (or filtered out by a permissions change), the latest
// `/api/chatbots` / `/api/org/users` response will no longer contain
// the picked id and we drop it from the request URL. Without that, the
// page silently produces an empty audit table because the server keeps
// receiving an orphan id.
//
// These helpers are extracted (and re-imported by the page) so the
// behavior can be unit-tested without spinning up a real DOM.

export interface FilterableTool {
  id: string;
  // Server-tagged grouping key: "builtin" | "servicenow" | "workday"
  // | "custom:<slug>". Older fixtures may omit it; we treat missing as
  // "builtin" (matching how the page renders untagged tools).
  integration?: string;
}

// Minimal shape needed to validate that a picked chatbot/user id is
// still present in the latest server response.
export interface FilterableEntity {
  id: string;
}

export interface AuditFilterState {
  status: string;
  chatbotId: string;
  integration: string;
  toolId: string;
  userId: string;
  from: string;
  to: string;
}

// Narrow the Tool dropdown to only tools owned by the picked integration.
// "all" means show every tool. Tools without an `integration` tag are
// treated as built-in (matches the server tagging behavior).
export function computeVisibleTools<T extends FilterableTool>(
  tools: T[] | undefined | null,
  integration: string,
): T[] {
  if (!tools) return [];
  if (integration === "all") return tools;
  return tools.filter((t) => (t.integration ?? "builtin") === integration);
}

// If the currently-picked tool is no longer in the visible list (because
// the integration filter narrowed it away), fall back to "all" so we
// don't send a stale toolId that produces an empty result page.
export function resolveEffectiveToolId<T extends FilterableTool>(
  toolId: string,
  visibleTools: T[],
): string {
  if (toolId === "all") return "all";
  if (visibleTools.some((t) => t.id === toolId)) return toolId;
  return "all";
}

// Generic version of the same "stale id is silently dropped" rule used
// by the Bot and User dropdowns. While the entity list is still loading
// (undefined / null), we collapse to "all" rather than risk re-sending
// a possibly-orphan id — matching how `resolveEffectiveToolId` behaves
// when `tools` hasn't loaded (computeVisibleTools returns []).
export function resolveEffectiveEntityId<T extends FilterableEntity>(
  selectedId: string,
  entities: T[] | undefined | null,
): string {
  if (selectedId === "all") return "all";
  if (!entities) return "all";
  if (entities.some((e) => e.id === selectedId)) return selectedId;
  return "all";
}

// Path of the audit-log API endpoint. Both the on-screen list and the
// "Export CSV" button hit this same route — CSV mode is selected by
// adding `format=csv` (see `buildAuditCsvUrl`).
export const AUDIT_TOOL_INVOCATIONS_PATH = "/api/audit/tool-invocations";

// Build the querystring sent to /api/audit/tool-invocations from the
// current filter state. Accepts the supporting lists so it can resolve
// the "stale id" cases (toolId vs integration filter, chatbotId /
// userId vs deleted rows) the same way the page's memos do.
export function buildAuditQueryString<
  T extends FilterableTool,
  C extends FilterableEntity,
  U extends FilterableEntity,
>(
  state: AuditFilterState,
  tools: T[] | undefined | null,
  chatbots?: C[] | null,
  users?: U[] | null,
): string {
  const visibleTools = computeVisibleTools(tools, state.integration);
  const effectiveToolId = resolveEffectiveToolId(state.toolId, visibleTools);
  const effectiveChatbotId = resolveEffectiveEntityId(state.chatbotId, chatbots);
  const effectiveUserId = resolveEffectiveEntityId(state.userId, users);
  const p = new URLSearchParams();
  if (state.status !== "all") p.set("status", state.status);
  if (effectiveChatbotId !== "all") p.set("chatbotId", effectiveChatbotId);
  if (state.integration !== "all") p.set("integration", state.integration);
  if (effectiveToolId !== "all") p.set("toolId", effectiveToolId);
  if (effectiveUserId !== "all") p.set("userId", effectiveUserId);
  if (state.from) p.set("from", state.from);
  if (state.to) {
    // Inclusive end-of-day if user picked a bare date.
    p.set("to", /T/.test(state.to) ? state.to : `${state.to}T23:59:59.999Z`);
  }
  p.set("limit", "500");
  return p.toString();
}

// Compose the URL the "Export CSV" button navigates to. Reuses the
// exact same filter querystring as the on-screen audit request and
// just adds `format=csv` so the server returns a CSV stream instead
// of JSON. If the filter portion drifts away from
// `buildAuditQueryString` the CSV would silently dump a different
// (usually wider) result set than what the admin is looking at.
//
// We deliberately strip the `limit` parameter that the on-screen
// request pins to a small page size: the CSV branch streams from the
// database one row at a time and has its own (much higher) cap on
// the server, so reviewers can pull a whole reporting window in a
// single download without being silently truncated to the JSON
// page size. If the server cap is hit the export ends with a marker
// row (status="truncated") so reviewers can tell.
export function buildAuditCsvUrl<
  T extends FilterableTool,
  C extends FilterableEntity,
  U extends FilterableEntity,
>(
  state: AuditFilterState,
  tools: T[] | undefined | null,
  chatbots?: C[] | null,
  users?: U[] | null,
): string {
  const params = new URLSearchParams(
    buildAuditQueryString(state, tools, chatbots, users),
  );
  params.delete("limit");
  params.set("format", "csv");
  return `${AUDIT_TOOL_INVOCATIONS_PATH}?${params.toString()}`;
}
